// https://react-spectrum.adobe.com/react-aria/useToast.html

import {memo, useRef} from "react";
import type {AriaToastProps} from "@react-aria/toast";
import type {ToastState, QueuedToast} from "@react-stately/toast";
import {useToast} from "@react-aria/toast";
import RippleButton from "../RippleButton";

import styles from "./index.module.css";

// https://react-spectrum.adobe.com/react-aria/useToast.html#typescript
export interface ToastType {
  title: string;
  type?: "info" | "success" | "error" | "warning";
}

interface ToastProps extends AriaToastProps<ToastType> {
  state: ToastState<ToastType>;
  toast: QueuedToast<ToastType>;
}

// Toast component that renders custom toast content.
const Toast = ({state, ...props}: ToastProps) => {
  const ref = useRef(null);
  const {toastProps, contentProps, titleProps, closeButtonProps} =
    useToast<ToastType>(props, state, ref);
  const {onPress} = closeButtonProps;
  const type = props.toast.content.type || "info";

  return (
    <div
      {...toastProps}
      ref={ref}
      className={`${styles.toast} absolute top-6 drop-shadow-2xl group-hover:relative`}
    >
      <div
        {...contentProps}
        className={[
          "border-1.5 mx-2 max-w-[512px] rounded-full px-6 py-4 pb-[14px] text-center shadow-2xl drop-shadow-2xl select-none sm:px-8 sm:pb-4",
          type === "info" ? "bg-info text-on-info border-on-info" : "",
          type === "success"
            ? "bg-success text-on-success border-on-success"
            : "",
          type === "error"
            ? "bg-error-container text-on-error-container border-on-error-container"
            : "",
          type === "warning"
            ? "bg-warning text-on-warning border-on-warning"
            : "",
        ].join(" ")}
      >
        <span {...titleProps}>{props.toast.content.title}</span>

        {!props.toast.timeout && (
          <RippleButton
            variant="icon"
            // RippleButton hands back a React MouseEvent while react-aria's
            // close handler expects its own PressEvent. The cast bridges the
            // two; the shape is derived from onPress rather than widened.
            onClick={(e) =>
              onPress?.(e as unknown as Parameters<typeof onPress>[0])
            }
          >
            x
          </RippleButton>
        )}
      </div>
    </div>
  );
};

export default memo(Toast);
